/** Content-free capture-journey evidence shared by the browser and server. */
export const RESEARCH_SCHEMA_VERSION = 1 as const;

export const RESEARCH_EVENT_NAMES = [
  'first_run.entered',
  'onboarding.entered',
  'onboarding.completed',
  'example.offered',
  'example.started',
  'example.completed',
  'example.skipped',
  'first_task.entered',
  'journey.entered',
  'journey.prepared',
  'journey.submitted',
  'journey.cancelled',
  'journey.submission_failed',
  'capture.started',
  'capture.stopped',
  'capture.permission_failed',
  'capture.processing_started',
  'capture.processing_finished',
  'capture.processing_failed',
  'browser.acknowledged',
  'browser.lesson_displayed',
  'browser.local_draft_saved',
  'browser.blocker_displayed',
  'route.task_started',
  'route.wrong_turn',
  'route.outcome_displayed',
  'route.outcome_saved',
  'raid.preview_entered',
  'raid.preview_completed',
  'raid.entry_viewed',
  'raid.entry_blocked',
  'raid.launch_succeeded',
  'raid.launch_refused',
  'client.queue_loss',
  'browser.activity_indicator_shown',
  'server.activity_available',
  'server.activity_opened',
  'server.activity_action_saved',
  'server.activity_resolved',
  'server.moment_saved',
  'server.creation_blocked',
  'server.creation_failed',
  'server.capture_processing_finished',
  'server.capture_processing_failed',
] as const;
export type ResearchEventName = (typeof RESEARCH_EVENT_NAMES)[number];
export const BROWSER_RESEARCH_EVENT_NAMES = RESEARCH_EVENT_NAMES.filter((name) => !name.startsWith('server.'));
export type BrowserResearchEventName = (typeof BROWSER_RESEARCH_EVENT_NAMES)[number];

export const RESEARCH_SURFACES = ['home', 'capture', 'moment', 'practice', 'onboarding', 'social', 'raids', 'system'] as const;
export type ResearchSurface = (typeof RESEARCH_SURFACES)[number];
export const RESEARCH_ENTRY_POINTS = ['home', 'moment', 'onboarding', 'activity', 'direct', 'unknown'] as const;
export type ResearchEntryPoint = (typeof RESEARCH_ENTRY_POINTS)[number];
export const CAPTURE_VARIANTS = ['written', 'recorded', 'listen', 'none', 'raid_preview', 'raid_real', 'navigation'] as const;
/** Raid attempts are not capture attempts: owner capture totals and cohorts leave them out (`raidsOf` in the store). */
export const isRaidVariant = (v: string): boolean => v === 'raid_preview' || v === 'raid_real';
/** `navigation` marks a route-discovery attempt (ticket 55); it is not a capture and never counts as one. */
export const isNavigationVariant = (v: string): boolean => v === 'navigation';
export type CaptureVariant = (typeof CAPTURE_VARIANTS)[number];
export const RESEARCH_REASONS = [
  'user_cancelled', 'submission_failed', 'server_unavailable', 'processing_failed', 'unknown',
  'daily_review', 'daily_allowance', 'ai_allowance', 'beta_budget', 'offline',
  'storage_full', 'storage_size', 'storage_owner', 'storage_unavailable',
  'raid_no_words', 'raid_resting', 'raid_limit', 'raid_pending', 'raid_full', 'raid_no_town', 'raid_not_friends', 'raid_refused',
  'permission_denied', 'microphone_unavailable', 'capture_failed',
  'left_before_task',
] as const;
export type ResearchReason = (typeof RESEARCH_REASONS)[number];

/**
 * Ticket 55's pinned hypothesis: a stable creation verb (Record / Type) and a named practice entry with one exit frame
 * (the material, the work, what is saved, where Back goes) lead a newcomer to the intended task with fewer wrong turns
 * than the earlier unnamed entries. The id is fixed here so every event and report names the same claim; a changed claim
 * gets a new id. The baseline is the cohort of earlier releases (which emit no route events) and observed newcomer
 * attempts; click counts alone never validate it.
 */
export const ROUTE_HYPOTHESIS = 'routes-v1' as const;
export const ROUTE_HYPOTHESES = [ROUTE_HYPOTHESIS] as const;
/** The task a participant set out to do: what the entry they used promises. */
export const ROUTE_TASKS = ['practice_first_look', 'practice_repeat', 'practice_direct'] as const;
export type RouteTask = (typeof ROUTE_TASKS)[number];
export const isRouteEvent = (name: string): boolean => name.startsWith('route.');

/** Unknown keys are rejected; learner text and other content cannot be event properties. */
export interface ResearchProperties {
  reason?: ResearchReason;
  /** Bounded counts reported by the browser delivery owner after local loss. */
  dropped?: number;
  expired?: number;
  activityKind?: 'friend_request' | 'incoming_army' | 'battle_report' | 'spoils';
  activityOutcome?: 'accepted' | 'declined' | 'defended' | 'auto_played' | 'report_read' | 'rebuilt' | 'withdrawn' | 'expired' | 'cancelled' | 'planted' | 'dropped';
  /** Route events only (ticket 55): the pinned hypothesis and the task the entry promised. */
  hypothesis?: (typeof ROUTE_HYPOTHESES)[number];
  task?: RouteTask;
}

export interface ResearchEventEnvelope {
  schemaVersion: typeof RESEARCH_SCHEMA_VERSION;
  eventId: string;
  eventName: ResearchEventName;
  subjectId: string;
  sessionId: string;
  attemptId: string;
  occurredAt: string;
  /** Monotonic milliseconds since the browser opened this attempt. */
  clientElapsedMs?: number;
  release: string;
  surface: ResearchSurface;
  entryPoint: ResearchEntryPoint;
  languagePair: string;
  interfaceLanguage: string;
  captureVariant: CaptureVariant;
  properties: ResearchProperties;
}

export interface AcceptedResearchEvent extends ResearchEventEnvelope {
  receivedAt: string;
  receivedRelease: string;
}

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SUBJECT = /^[0-9a-f]{32}$/;
const LANGUAGE_PAIR = /^[a-z]{2,3}-[a-z]{2,3}$/;
const INTERFACE_LANGUAGE = /^[a-z]{2,3}$/;
const SAFE_RELEASE = /^[\w.+-]{1,80}$/;
const MAX_CLIENT_ELAPSED_MS = 7 * 24 * 60 * 60 * 1000;

function member<T extends readonly string[]>(values: T, value: unknown): value is T[number] {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}

/** Route events carry exactly a pinned hypothesis and a task; other events carry neither. */
export function validRouteProperties(eventName: string, p: Record<string, unknown>): boolean {
  if (!isRouteEvent(eventName)) return p.hypothesis === undefined && p.task === undefined;
  if (!member(ROUTE_HYPOTHESES, p.hypothesis) || !member(ROUTE_TASKS, p.task)) return false;
  return eventName === 'route.wrong_turn' ? p.reason === 'left_before_task' : p.reason === undefined;
}

/** Strict boundary parser. Unknown envelope and property fields are never stored. */
export function parseResearchEnvelope(raw: unknown, _now = new Date()): ResearchEventEnvelope | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  const keys = new Set([
    'schemaVersion', 'eventId', 'eventName', 'subjectId', 'sessionId', 'attemptId', 'occurredAt',
    'clientElapsedMs', 'release', 'surface', 'entryPoint', 'languagePair', 'interfaceLanguage', 'captureVariant', 'properties',
  ]);
  if (Object.keys(value).some((key) => !keys.has(key))) return null;
  if (value.schemaVersion !== RESEARCH_SCHEMA_VERSION || !ID.test(String(value.eventId ?? '')) ||
      !member(RESEARCH_EVENT_NAMES, value.eventName) || !SUBJECT.test(String(value.subjectId ?? '')) ||
      !ID.test(String(value.sessionId ?? '')) || !ID.test(String(value.attemptId ?? '')) ||
      typeof value.occurredAt !== 'string' || !Number.isFinite(Date.parse(value.occurredAt)) ||
      !SAFE_RELEASE.test(String(value.release ?? '')) || !member(RESEARCH_SURFACES, value.surface) ||
      !member(RESEARCH_ENTRY_POINTS, value.entryPoint) || !LANGUAGE_PAIR.test(String(value.languagePair ?? '')) ||
      !INTERFACE_LANGUAGE.test(String(value.interfaceLanguage ?? '')) || !member(CAPTURE_VARIANTS, value.captureVariant)) return null;
  if (value.clientElapsedMs !== undefined &&
      (typeof value.clientElapsedMs !== 'number' || !Number.isFinite(value.clientElapsedMs) ||
       value.clientElapsedMs < 0 || value.clientElapsedMs > MAX_CLIENT_ELAPSED_MS)) return null;

  const occurred = Date.parse(value.occurredAt);
  const rawProperties = value.properties;
  if (!rawProperties || typeof rawProperties !== 'object' || Array.isArray(rawProperties)) return null;
  const p = rawProperties as Record<string, unknown>;
  if (Object.keys(p).some((key) => !['reason', 'dropped', 'expired', 'activityKind', 'activityOutcome', 'hypothesis', 'task'].includes(key))) return null;
  if (!validRouteProperties(String(value.eventName), p)) return null;
  if (p.reason !== undefined && !member(RESEARCH_REASONS, p.reason)) return null;
  if (p.activityKind !== undefined && !member(['friend_request', 'incoming_army', 'battle_report', 'spoils'] as const, p.activityKind)) return null;
  if (p.activityOutcome !== undefined && !member(['accepted', 'declined', 'defended', 'auto_played', 'report_read', 'rebuilt', 'withdrawn', 'expired', 'cancelled', 'planted', 'dropped'] as const, p.activityOutcome)) return null;
  const isLoss = value.eventName === 'client.queue_loss';
  for (const key of ['dropped', 'expired'] as const) {
    if (p[key] !== undefined && (!Number.isSafeInteger(p[key]) || Number(p[key]) < 0 || Number(p[key]) > 1_000_000)) return null;
    if (p[key] !== undefined && !isLoss) return null;
  }
  if (isLoss && (p.dropped === undefined && p.expired === undefined ||
      Number(p.dropped ?? 0) + Number(p.expired ?? 0) < 1)) return null;

  return {
    schemaVersion: RESEARCH_SCHEMA_VERSION,
    eventId: String(value.eventId).toLowerCase(),
    eventName: value.eventName,
    subjectId: String(value.subjectId),
    sessionId: String(value.sessionId).toLowerCase(),
    attemptId: String(value.attemptId).toLowerCase(),
    occurredAt: new Date(occurred).toISOString(),
    ...(typeof value.clientElapsedMs === 'number' ? { clientElapsedMs: value.clientElapsedMs } : {}),
    release: String(value.release),
    surface: value.surface,
    entryPoint: value.entryPoint,
    languagePair: String(value.languagePair).toLowerCase(),
    interfaceLanguage: String(value.interfaceLanguage).toLowerCase(),
    captureVariant: value.captureVariant,
    properties: {
      ...(p.reason === undefined ? {} : { reason: p.reason as ResearchReason }),
      ...(p.dropped === undefined ? {} : { dropped: Number(p.dropped) }),
      ...(p.expired === undefined ? {} : { expired: Number(p.expired) }),
      ...(p.activityKind === undefined ? {} : { activityKind: p.activityKind as ResearchProperties['activityKind'] }),
      ...(p.activityOutcome === undefined ? {} : { activityOutcome: p.activityOutcome as ResearchProperties['activityOutcome'] }),
      ...(p.hypothesis === undefined ? {} : { hypothesis: p.hypothesis as ResearchProperties['hypothesis'] }),
      ...(p.task === undefined ? {} : { task: p.task as RouteTask }),
    },
  };
}

export const isBrowserResearchEventName = (value: unknown): value is BrowserResearchEventName =>
  typeof value === 'string' && (BROWSER_RESEARCH_EVENT_NAMES as readonly string[]).includes(value);
